import { useEffect, useState, useCallback, useRef } from "react";
import { DropZone } from "@/components/DropZone";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, API, fmtDate, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { useSuspendedClientGuard } from "@/context/SuspendedClientAlert";
import { SourceIcon } from "@/components/SourceIcon";
import { InternalLayout } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RichEditor, SafeHtml, isEmptyHtml } from "@/components/RichEditor";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge, avatarColor, initial, timeAgo, SlaBadge } from "@/pages/TicketList";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeftIcon as ArrowLeft, PaperclipIcon as Paperclip, DownloadSimpleIcon as DownloadSimple, TrashIcon as Trash, ChatCircleIcon as ChatCircle, NotePencilIcon as NotePencil, ClockCounterClockwiseIcon as ClockCounterClockwise, LightningIcon as Lightning, CaretRightIcon as CaretRight, ArrowUUpLeftIcon as ArrowUUpLeft, CheckCircleIcon as CheckCircle, PaperPlaneTiltIcon as PaperPlaneTilt, TimerIcon as Timer, ArrowsClockwiseIcon as ArrowsClockwise, ToolboxIcon as Toolbox, LinkSimpleIcon as LinkSimple, PlusIcon as Plus, MagnifyingGlassIcon as MagnifyingGlass, ArrowSquareOutIcon as ArrowSquareOut, WarningIcon as Warning, X, EyeIcon as Eye, ProhibitIcon as Prohibit, UserIcon as User, EnvelopeSimpleIcon as Envelope, PhoneIcon as Phone } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

function ClientPicker({ clients, value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = clients.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between gap-2 rounded-sm border border-input bg-white px-3 h-9 text-sm text-left hover:bg-secondary/50"
          data-testid="prop-cliente-trigger"
        >
          <span className={current ? "font-medium text-slate-700 truncate" : "text-muted-foreground truncate"}>
            {current ? current.ragione_sociale : "Nessun cliente"}
          </span>
          <CaretRight size={13} className="shrink-0 rotate-90 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[280px] rounded-sm" align="start">
        <Command>
          <CommandInput placeholder="Cerca cliente..." data-testid="prop-cliente-search" />
          <CommandList>
            <CommandEmpty>Nessun cliente trovato</CommandEmpty>
            <CommandGroup>
              {value && (
                <CommandItem value="__none__" onSelect={() => { onChange(null); setOpen(false); }} data-testid="prop-cliente-none">
                  <span className="text-muted-foreground">— Rimuovi collegamento</span>
                </CommandItem>
              )}
              {clients.map((c) => (
                <CommandItem key={c.id} value={c.ragione_sociale} onSelect={() => { onChange(c.id); setOpen(false); }} data-testid={`prop-cliente-opt-${c.id}`}>
                  {c.ragione_sociale}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const MSG_META = {
  cliente: { border: "border-l-blue-400", bg: "bg-blue-50/40", label: "Cliente" },
  operatore: { border: "border-l-emerald-400", bg: "bg-emerald-50/40", label: "Operatore" },
  nota_interna: { border: "border-l-amber-400", bg: "bg-amber-50/60", label: "Nota interna" },
  sistema: { border: "border-l-slate-300", bg: "bg-slate-50", label: "Sistema" },
};

const highlightMentions = (text, users) => {
  const names = (users || []).map((u) => u?.name).filter(Boolean);
  if (!names.length || !text) return text;
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const rx = new RegExp(`@(?:${escaped.join("|")})`, "g");
  const parts = [];
  let last = 0, m, i = 0;
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<span key={`mn-${i++}`} className="text-blue-700 bg-blue-100 rounded px-1 font-medium" data-testid="mention-chip">{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const PresenceAvatars = ({ presence }) => {
  const others = (presence || []).filter((p) => !p.is_self);
  if (!others.length) return null;
  const shown = others.slice(0, 4);
  const extra = others.length - shown.length;
  const listTitle = "Ticket visualizzato da:\n" + others.map((o) => `• ${o.name}`).join("\n");
  return (
    <div className="flex items-center mr-1" data-testid="ticket-presence">
      <div className="flex items-center -space-x-2" title={listTitle}>
        {shown.map((o) => (
          <div key={o.user_id} title={`Ticket visualizzato da: ${o.name}`} data-testid={`presence-avatar-${o.user_id}`}
               className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-slate-700 ring-2 ring-white shadow-sm select-none"
               style={{ backgroundColor: avatarColor(o.name || o.email || "?") }}>
            {o.initials}
          </div>
        ))}
        {extra > 0 && (
          <div title={listTitle} data-testid="presence-more"
               className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-slate-700 bg-slate-200 ring-2 ring-white shadow-sm select-none">
            +{extra}
          </div>
        )}
      </div>
    </div>
  );
};


export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const [t, setT] = useState(null);
  useSuspendedClientGuard(t?.cliente_id);
  const [presence, setPresence] = useState([]);
  const [csatAsk, setCsatAsk] = useState(false);
  const [csatEnabled, setCsatEnabled] = useState(true);  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) sessionIdRef.current = (window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
  const [activeTab, setActiveTab] = useState("conversazione");
  const forcedTabRef = useRef(null);
  const forcedModeRef = useRef(null);
  const [meta, setMeta] = useState({ statuses: [], priorities: [], sources: [], groups: [], users: [], clients: [], contracts: [], slaPolicies: [] });
  const [msgs, setMsgs] = useState([]);
  const [hist, setHist] = useState([]);
  const [atts, setAtts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [slaEvents, setSlaEvents] = useState([]);
  const [delOpen, setDelOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [linkDlg, setLinkDlg] = useState(false);
  const [linkQ, setLinkQ] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [suggestDismissed, setSuggestDismissed] = useState(false);
  const [mode, setMode] = useState("rispondi");
  const [body, setBody] = useState("");
  const [replyVisible, setReplyVisible] = useState(true);
  const [sending, setSending] = useState(false);
  const [mailboxes, setMailboxes] = useState([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [cc, setCc] = useState("");
  const [toAddr, setToAddr] = useState("");
  const [notifyUsers, setNotifyUsers] = useState([]);
  const [mailboxId, setMailboxId] = useState("");
  const [uploading, setUploading] = useState(false);
  const composerRef = useRef(null);
  const fileRef = useRef(null);
  const composerFileRef = useRef(null);
  const [pendingAtts, setPendingAtts] = useState([]); // allegati del messaggio in composizione
  const [uploadingComposer, setUploadingComposer] = useState(false);
  // Ridimensionamento colonna Proprietà (solo desktop, persistito in localStorage)
  const PROP_DEFAULT_W = 384, PROP_MIN_W = 320, PROP_MAX_W = 650;
  const colsRef = useRef(null);
  const [isLg, setIsLg] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width:1024px)").matches);
  const [propWidth, setPropWidth] = useState(() => {
    const s = parseInt(localStorage.getItem("helpdesk_ticket_properties_width"), 10);
    return s >= PROP_MIN_W && s <= PROP_MAX_W ? s : PROP_DEFAULT_W;
  });
  useEffect(() => {
    const mq = window.matchMedia("(min-width:1024px)");
    const h = (e) => setIsLg(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  useEffect(() => { localStorage.setItem("helpdesk_ticket_properties_width", String(propWidth)); }, [propWidth]);
  // Presenza live: heartbeat + polling ogni 15s, cleanup all'uscita (best-effort, TTL lato server)
  useEffect(() => {
    if (!id) return;
    let alive = true;
    const sid = sessionIdRef.current;
    const beat = async () => {
      try {
        await api.post(`/tickets/${id}/presence/heartbeat`, { session_id: sid, user_agent: navigator.userAgent });
        const { data } = await api.get(`/tickets/${id}/presence`);
        if (alive) setPresence(Array.isArray(data) ? data : []);
      } catch { if (alive) setPresence([]); }
    };
    beat();
    const iv = setInterval(beat, 15000);
    const leaveBeacon = () => {
      try {
        fetch(`${API}/tickets/${id}/presence?session_id=${sid}`, { method: "DELETE", credentials: "include", keepalive: true });
      } catch { /* noop */ }
    };
    window.addEventListener("beforeunload", leaveBeacon);
    return () => {
      alive = false;
      clearInterval(iv);
      window.removeEventListener("beforeunload", leaveBeacon);
      api.delete(`/tickets/${id}/presence`, { params: { session_id: sid } }).catch(() => {});
    };
  }, [id]);

  const startPropResize = (e) => {
    if (!isLg || !colsRef.current) return;
    e.preventDefault();
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    const onMove = (ev) => {
      const right = colsRef.current.getBoundingClientRect().right;
      const w = Math.max(PROP_MIN_W, Math.min(PROP_MAX_W, right - ev.clientX));
      setPropWidth(w);
    };
    const onUp = () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const [mentions, setMentions] = useState([]);        // utenti menzionati nella nota corrente {id,name}
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightMsgId, setHighlightMsgId] = useState(null);
  const msgDeepLinkDone = useRef(false);
  const MAX_ATT_BYTES = 100 * 1024 * 1024;
  const addMention = (u) => setMentions((prev) => (prev.find((p) => p.id === u.id) ? prev : [...prev, u]));
  const [editTitle, setEditTitle] = useState(false);
  const [titleVal, setTitleVal] = useState("");
  const [editReq, setEditReq] = useState(false);
  const [reqForm, setReqForm] = useState({ nome: "", email: "", telefono: "" });
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [editMentions, setEditMentions] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const addEditMention = (u) => setEditMentions((prev) => (prev.find((p) => p.id === u.id) ? prev : [...prev, u]));

  const load = useCallback(() => {
    api.get(`/tickets/${id}`).then(({ data }) => {
      setT(data);
      if (data.has_completed_linked_activity && forcedTabRef.current !== id) {
        forcedTabRef.current = id;
        setActiveTab("interventi");
      }
      // Default composer: Nota interna per sorgenti non conversazionali (Telefono/Interno/Monitoraggio/Rapportino)
      if (forcedModeRef.current !== id) {
        forcedModeRef.current = id;
        const srcName = data.sorgente?.nome || "";
        const noteSources = ["Telefono", "Interno", "Monitoraggio", "Rapportino", "Follow-up rapportino"];
        if (noteSources.includes(srcName) || data.source_context === "nota_rapportino") setMode("nota");
        else setMode("rispondi");
      }
    }).catch(() => toast.error("Ticket non trovato"));
    api.get(`/tickets/${id}/messages`).then(({ data }) => setMsgs(data)).catch(() => {});
    api.get(`/tickets/${id}/history`).then(({ data }) => setHist(data)).catch(() => {});
    api.get(`/tickets/${id}/attachments`).then(({ data }) => setAtts(data)).catch(() => {});
    api.get(`/ticket-automation-logs`, { params: { ticket_id: id } }).then(({ data }) => setLogs(data)).catch(() => {});
    api.get(`/hd-tickets/${id}/sla/events`).then(({ data }) => setSlaEvents(data)).catch(() => {});
    api.get(`/tickets/${id}/activities`).then(({ data }) => setActivities(data)).catch(() => {});
  }, [id]);
  useEffect(() => {
    Promise.all([api.get("/ticket-statuses"), api.get("/ticket-priorities"), api.get("/ticket-sources"),
      api.get("/ticket-groups"), api.get("/tecnici"),
      api.get("/clients"), api.get("/contracts"), api.get("/hd-sla-policies")]).then(([st, pr, src, grp, usr, cli, ctr, sla]) =>
      setMeta({ statuses: st.data, priorities: pr.data, sources: src.data, groups: grp.data, users: [...usr.data].sort((a, b) => (a.name || "").localeCompare(b.name || "")), clients: cli.data, contracts: ctr.data, slaPolicies: sla.data })).catch(() => {});
    api.get("/hd-ticket-mailboxes-options").then(({ data }) => {
      setMailboxes(data);
      if (data.length) { const def = data.find((m) => m.is_default_outbound) || data[0]; setMailboxId(def.id); }
    }).catch(() => {});
    api.get("/ticket-settings").then(({ data }) => setCsatEnabled(data.csat_enabled !== false)).catch(() => {});
    load();
  }, [load]);

  // Deep-link da notifica citazione: apre la conversazione e scorre al messaggio esatto (?msg=<id>)
  useEffect(() => {
    const mid = searchParams.get("msg");
    if (!mid || !msgs.length || msgDeepLinkDone.current) return;
    const el = document.getElementById(`hd-msg-${mid}`);
    if (!el) return;
    msgDeepLinkDone.current = true;
    setActiveTab("conversazione");
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightMsgId(mid);
    setTimeout(() => setHighlightMsgId(null), 3000);
    const sp = new URLSearchParams(searchParams);
    sp.delete("msg");
    setSearchParams(sp, { replace: true });
  }, [msgs, searchParams, setSearchParams]);

  useEffect(() => {
    if (t?.richiedente_email) setToAddr((prev) => prev || t.richiedente_email);
    if (t && mailboxes.length > 0 && t.sorgente?.nome === "Email" && t.richiedente_email) {
      setSendEmail(true);
    }
  }, [t?.id, t?.sorgente?.nome, mailboxes.length]); // eslint-disable-line

  // Cambio destinatario principale: aggiunge automaticamente il mittente originale in CC (modificabile)
  const changeTo = (val) => {
    const orig = (t?.richiedente_email || "").trim();
    setToAddr(val);
    if (orig && val.trim().toLowerCase() !== orig.toLowerCase()) {
      setCc((prev) => {
        const list = (prev || "").split(/[,;]/).map((s) => s.trim()).filter(Boolean);
        if (!list.some((e) => e.toLowerCase() === orig.toLowerCase())) list.push(orig);
        return list.join(", ");
      });
    }
  };

  const patch = async (field, value) => {
    try {
      await api.put(`/tickets/${id}`, { [field]: value });
      if (field === "stato_id") {
        const st = meta.statuses.find((s) => s.id === value);
        if (st && (st.stato_finale || st.blocca_modifiche)) { load(); maybeAskCsat(); return; }
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };
  const focusComposer = (m) => { setMode(m); composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); setTimeout(() => composerRef.current?.querySelector(".ql-editor")?.focus(), 100); };
  const uploadInlineImage = useCallback(async (file) => {
    if (file.size > MAX_ATT_BYTES) { toast.error("Immagine troppo grande (max 100 MB)"); return null; }
    const fd = new FormData(); fd.append("files", file);
    const { data } = await api.post(`/tickets/${id}/attachments`, fd);
    const a = data.added?.[0];
    if (!a) return null;
    return `${process.env.REACT_APP_BACKEND_URL}/api/tickets/${id}/attachments/${a.id}/inline`;
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const addComposerFiles = async (files) => {
    const arr = Array.from(files || []); if (!arr.length) return;
    const big = arr.find((f) => f.size > MAX_ATT_BYTES);
    if (big) { toast.error(`File troppo grande "${big.name}". Dimensione massima consentita: 100 MB.`); return; }
    setUploadingComposer(true);
    const tid = toast.loading("Caricamento allegato in corso…");
    try {
      for (const f of arr) {
        const fd = new FormData(); fd.append("files", f);
        const { data } = await api.post(`/tickets/${id}/attachments`, fd);
        const a = data.added?.[0];
        if (a) setPendingAtts((p) => [...p, { id: a.id, filename: a.filename, size: a.size }]);
      }
      toast.success("Allegato pronto", { id: tid });
    } catch (err) {
      toast.error(err?.response?.status === 413 ? "File troppo grande (max 100 MB)." : "Errore durante il caricamento", { id: tid });
    } finally {
      setUploadingComposer(false);
      if (composerFileRef.current) composerFileRef.current.value = "";
    }
  };
  const removePending = async (aid) => { try { await api.delete(`/tickets/${id}/attachments/${aid}`); } catch { /* noop */ } setPendingAtts((p) => p.filter((x) => x.id !== aid)); };

  const send = async () => {
    if (isEmptyHtml(body) && pendingAtts.length === 0) return;
    if (uploadingComposer) { toast.error("Attendi il completamento del caricamento allegati"); return; }
    setSending(true);
    const attIds = pendingAtts.map((a) => a.id);
    try {
      if (mode === "nota") {
        const norm = (body || "").replace(/&nbsp;|\u00a0/g, " ");
        const mids = mentions.filter((m) => norm.includes(`@${m.name}`)).map((m) => m.id);
        const notifyIds = notifyUsers.map((u) => u.id);
        await api.post(`/tickets/${id}/messages`, { tipo: "nota_interna", corpo_html: body, mentioned_user_ids: mids, notify_user_ids: notifyIds, attachment_ids: attIds });
        const tot = new Set([...mids, ...notifyIds]).size;
        toast.success(tot ? `Nota interna aggiunta · ${tot} notifica/e inviata/e` : "Nota interna aggiunta");
        setMentions([]); setNotifyUsers([]);
      } else {
        const payload = { tipo: "operatore", corpo_html: body, visibile_cliente: replyVisible, attachment_ids: attIds };
        if (sendEmail && replyVisible) { payload.invia_email = true; payload.to = toAddr; payload.cc = cc; if (mailboxId) payload.mailbox_id = mailboxId; }
        const { data } = await api.post(`/tickets/${id}/messages`, payload);
        if (payload.invia_email && !data.inviato_email) toast.error(`Risposta salvata ma email NON inviata: ${data.esito_invio_email || "errore"}`);
        else if (data.inviato_email) toast.success(data.esito_invio_email || "Risposta inviata via email");
        else toast.success("Risposta aggiunta");
      }
      setBody(""); setCc(""); setPendingAtts([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
    finally { setSending(false); }
  };
  const saveTitle = async () => {
    const v = (titleVal || "").trim();
    if (!v) { toast.error("L'oggetto non può essere vuoto"); return; }
    try { await api.put(`/tickets/${id}`, { oggetto: v }); setEditTitle(false); load(); toast.success("Titolo aggiornato"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };
  const startEditReq = () => { setReqForm({ nome: t.richiedente_nome || "", email: t.richiedente_email || "", telefono: t.richiedente_telefono || "" }); setEditReq(true); };
  const saveReq = async () => {
    try { await api.put(`/tickets/${id}`, { richiedente_nome: reqForm.nome.trim(), richiedente_email: reqForm.email.trim(), richiedente_telefono: reqForm.telefono.trim() }); setEditReq(false); load(); toast.success("Referente aggiornato"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };
  const startEditMsg = (m) => { setEditingMsgId(m.id); setEditBody(m.corpo_html || ("<p>" + String(m.corpo || "").replace(/\n/g, "<br>") + "</p>")); setEditMentions(m.mentioned_users || []); };
  const saveEditMsg = async (m) => {
    if (isEmptyHtml(editBody)) { toast.error("Il contenuto non può essere vuoto"); return; }
    setSavingEdit(true);
    try {
      const payload = { corpo_html: editBody };
      if (m.tipo === "nota_interna") {
        const norm = (editBody || "").replace(/&nbsp;|\u00a0/g, " ");
        payload.mentioned_user_ids = editMentions.filter((x) => norm.includes(`@${x.name}`)).map((x) => x.id);
      }
      await api.put(`/tickets/${id}/messages/${m.id}`, payload);
      setEditingMsgId(null); load(); toast.success("Modifica salvata");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
    finally { setSavingEdit(false); }
  };
  const closeTicket = async () => {
    const st = meta.statuses.find((s) => s.blocca_modifiche) || meta.statuses.find((s) => s.stato_finale);
    if (!st) { toast.error("Nessuno stato di chiusura configurato"); return; }
    try { await api.put(`/tickets/${id}`, { stato_id: st.id }); load(); maybeAskCsat(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };
  const maybeAskCsat = () => { if (csatEnabled && t?.richiedente_email && !t?.csat?.status) setCsatAsk(true); };
  const sendCsat = async () => {
    try { const { data } = await api.post(`/tickets/${id}/csat/send`); toast.success(data.esito || "Sondaggio inviato"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Invio sondaggio fallito"); }
    finally { setCsatAsk(false); }
  };
  const sendAndClose = async () => {
    if (isEmptyHtml(body)) { toast.error("Scrivi una risposta prima di chiudere"); return; }
    if (mode === "nota") { toast.error("Per chiudere usa la modalità Rispondi, non Nota interna"); return; }
    if (uploadingComposer) { toast.error("Attendi il completamento del caricamento allegati"); return; }
    const st = meta.statuses.find((s) => s.blocca_modifiche) || meta.statuses.find((s) => s.stato_finale);
    if (!st) { toast.error("Nessuno stato di chiusura configurato"); return; }
    setSending(true);
    const attIds = pendingAtts.map((a) => a.id);
    try {
      const payload = { tipo: "operatore", corpo_html: body, visibile_cliente: replyVisible, attachment_ids: attIds };
      if (sendEmail && replyVisible) { payload.invia_email = true; payload.to = toAddr; payload.cc = cc; if (mailboxId) payload.mailbox_id = mailboxId; }
      const { data } = await api.post(`/tickets/${id}/messages`, payload);
      if (payload.invia_email && !data.inviato_email) {
        toast.error(`Invio email fallito: ${data.esito_invio_email || "errore"}. Il ticket NON è stato chiuso.`);
        setBody(""); setCc(""); setPendingAtts([]); load();
        return;
      }
      await api.put(`/tickets/${id}`, { stato_id: st.id });
      toast.success("Risposta inviata e ticket chiuso");
      load();
      maybeAskCsat();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore durante l'invio/chiusura");
    } finally { setSending(false); }
  };
  const reopenTicket = () => { const st = meta.statuses.find((s) => s.nome === "Aperto") || meta.statuses.find((s) => !s.stato_finale && !s.blocca_modifiche); if (st) patch("stato_id", st.id); };
  const doDelete = async () => { try { await api.delete(`/tickets/${id}`); toast.success("Ticket eliminato"); nav("/helpdesk/ticket"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); } };
  const blockSender = async () => {
    const e = (t?.richiedente_email || "").trim();
    if (!e) return;
    if (!window.confirm(`Bloccare "${e}"? Tutti i ticket ricevuti da questo indirizzo verranno ELIMINATI in modo permanente e le future email verranno ignorate.`)) return;
    try {
      const { data } = await api.post(`/tickets/${id}/block-sender`);
      toast.success(data.message || "Mittente bloccato", { description: `Ticket eliminati: ${data.deleted_tickets ?? 0}` });
      nav("/helpdesk/ticket");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore"); }
  };
  const loadActivities = () => api.get(`/tickets/${id}/activities`).then(({ data }) => setActivities(data)).catch(() => {});
  const createIntervento = () => nav(`/interventi/nuovo?from_ticket=${id}`);
  const searchLinkActivities = async (val) => {
    setLinkQ(val);
    try { const { data } = await api.get("/attivita/search-for-link", { params: { q: val, cliente_id: t?.cliente_id || "" } }); setLinkResults(data); } catch { setLinkResults([]); }
  };
  const linkActivity = async (aid, aCliente) => {
    if (aCliente && t?.cliente_id && aCliente !== t.cliente_id && !window.confirm("Il cliente dell'intervento è diverso da quello del ticket. Collegare comunque?")) return;
    try { await api.post(`/tickets/${id}/activities/link`, { attivita_id: aid, source: "linked_from_ticket" }); toast.success("Intervento collegato"); setLinkDlg(false); setLinkQ(""); setLinkResults([]); loadActivities(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };
  const unlinkActivity = async (aid) => { if (!window.confirm("Scollegare questo intervento dal ticket?")) return; try { await api.delete(`/tickets/${id}/activities/${aid}`); toast.success("Intervento scollegato"); loadActivities(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const resolveFromActivity = async () => { try { const { data } = await api.post(`/tickets/${id}/resolve-from-activity`); setT(data); toast.success("Ticket risolto"); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const recalcSla = async () => { try { await api.post(`/hd-tickets/${id}/sla/recalculate`); load(); toast.success("SLA ricalcolato"); } catch { toast.error("Errore ricalcolo SLA"); } };
  const setSlaPolicy = async (pid) => { try { await api.put(`/hd-tickets/${id}/sla/policy`, { sla_policy_id: pid === "none" ? null : pid }); load(); toast.success("Policy SLA aggiornata"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); } };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    const tooBig = files.find((ff) => ff.size > MAX_ATT_BYTES);
    if (tooBig) {
      toast.error(`File troppo grande "${tooBig.name}". Dimensione massima consentita: 100 MB.`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const fd = new FormData(); files.forEach((ff) => fd.append("files", ff));
    setUploading(true);
    const tid = toast.loading("Caricamento allegato in corso…");
    try {
      await api.post(`/tickets/${id}/attachments`, fd);
      toast.success("Allegato caricato correttamente", { id: tid });
      load();
    } catch (err) {
      const msg = err?.response?.status === 413 ? "File troppo grande. Dimensione massima consentita: 100 MB."
        : (formatApiErrorDetail(err?.response?.data?.detail) || "Errore durante il caricamento dell'allegato");
      toast.error(msg, { id: tid });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const _EXT_MIME = { png:"image/png", jpg:"image/jpeg", jpeg:"image/jpeg", gif:"image/gif", webp:"image/webp", bmp:"image/bmp", svg:"image/svg+xml", pdf:"application/pdf", txt:"text/plain", csv:"text/csv", heic:"image/heic", tif:"image/tiff", tiff:"image/tiff", doc:"application/msword", xls:"application/vnd.ms-excel" };
  const _MIME_EXT = { "image/png":".png","image/jpeg":".jpg","image/gif":".gif","image/webp":".webp","image/bmp":".bmp","image/svg+xml":".svg","application/pdf":".pdf","text/plain":".txt","text/csv":".csv","image/heic":".heic","image/tiff":".tiff" };
  const _extFromType = (t) => _MIME_EXT[(t||"").toLowerCase()] || "";
  const _typeFromName = (n) => { const m=(n||"").toLowerCase().match(/\.([a-z0-9]+)$/); return m ? (_EXT_MIME[m[1]]||"") : ""; };
  const downloadAtt = async (a) => {
    try {
      const res = await api.get(`/tickets/${id}/attachments/${a.id}/download`, { responseType: "blob" });
      let name = a.filename || "allegato";
      if (!/\.[a-z0-9]{2,5}$/i.test(name)) name += (_extFromType(res.data?.type || a.content_type) || _extFromType(_typeFromName(name)) || "");
      const url = window.URL.createObjectURL(res.data); const el = document.createElement("a");
      el.href = url; el.download = name; document.body.appendChild(el); el.click(); el.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };
  const previewAtt = async (a) => {
    try {
      const res = await api.get(`/tickets/${id}/attachments/${a.id}/inline`, { responseType: "blob" });
      let type = res.data?.type && res.data.type !== "application/octet-stream" ? res.data.type
        : (a.content_type && a.content_type !== "application/octet-stream" ? a.content_type : _typeFromName(a.filename));
      if (!type) type = "application/octet-stream";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Anteprima non disponibile"); }
  };
  const HIST_ACTIONS = { creazione:"Creazione", cambio_stato:"Stato", cambio_priorita:"Priorità", cambio_categoria:"Categoria", cambio_gruppo:"Gruppo", cambio_assegnatario:"Assegnazione", cambio_visibilita:"Visibilità cliente", messaggio:"Messaggio", nota_interna:"Nota interna", allegato:"Allegato", automazione:"Automazione", collegamento:"Collegamento intervento", eliminazione:"Eliminazione", riapertura_risposta_cliente:"Riapertura (risposta cliente)", intervento_completato:"Intervento completato", stato:"Stato", nota_rapportino:"Nota rapportino" };
  const histActionLabel = (act) => HIST_ACTIONS[act] || (act || "").replace(/_/g, " ");
  const idName = (v) => {
    if (!v || typeof v !== "string") return v;
    if (!/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-/.test(v)) return v;
    const all = [...(meta.statuses||[]),...(meta.priorities||[]),...(meta.sources||[]),...(meta.groups||[]),...(meta.users||[]),...(meta.clients||[]),...(meta.contracts||[])];
    const o = all.find((x)=>x.id===v);
    return o ? (o.nome||o.name||o.ragione_sociale||v) : v;
  };
  // Allegati "reali" di un messaggio (esclude le immagini inline già mostrate nel corpo, che hanno content_id)
  const msgAttachments = (m) => (atts || []).filter((a) => a.message_id === m.id && !a.content_id);
  const delAtt = async (aid) => { try { await api.delete(`/tickets/${id}/attachments/${aid}`); load(); } catch { toast.error("Errore"); } };

  if (!t) return <InternalLayout><p className="text-sm text-muted-foreground">Caricamento…</p></InternalLayout>;
  const isClosed = !!t.stato?.blocca_modifiche;
  const isCessato = t.cliente_stato === "cessato";
  const Prop = ({ label, field, coll, allowNone = true, required = false }) => (
    <div><Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}{required && <span className="text-rose-500"> *</span>}</Label>
      <Select value={t[field] || "none"} onValueChange={(v) => patch(field, v === "none" ? null : v)}>
        <SelectTrigger className="mt-1 rounded-sm h-9 bg-white" data-testid={`prop-${field}`}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>{allowNone && <SelectItem value="none">—</SelectItem>}{meta[coll].map((x) => <SelectItem key={x.id} value={x.id}>{x.nome || x.name}</SelectItem>)}</SelectContent>
      </Select></div>
  );

  return (
    <InternalLayout>
      <div className="max-w-[1400px] mx-auto">
        {/* Breadcrumb + toolbar */}
        <div className="sticky top-14 z-20 -mx-8 px-8 pt-3 pb-2.5 bg-background/95 backdrop-blur border-b border-border mb-5">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2.5">
            <button onClick={() => nav("/helpdesk/ticket")} className="hover:text-foreground hover:underline" data-testid="ticket-back">Tutti i ticket</button>
            <CaretRight size={13} /><span className="font-mono font-medium text-foreground" data-testid="ticket-numero">{t.numero_ticket}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="rounded-sm h-8 w-8"><ArrowLeft size={17} /></Button>
            <Button variant="outline" size="sm" onClick={() => focusComposer("rispondi")} disabled={isCessato} className="rounded-sm gap-1.5" data-testid="btn-rispondi"><ArrowUUpLeft size={15} /> Rispondi</Button>
            <Button variant="outline" size="sm" onClick={() => focusComposer("nota")} disabled={isCessato} className="rounded-sm gap-1.5" data-testid="btn-nota"><NotePencil size={15} /> Nota</Button>
            {isClosed
              ? <Button variant="outline" size="sm" onClick={reopenTicket} className="rounded-sm gap-1.5" data-testid="btn-riapri"><ArrowUUpLeft size={15} /> Riapri</Button>
              : <Button variant="outline" size="sm" onClick={closeTicket} className="rounded-sm gap-1.5" data-testid="btn-chiudi"><CheckCircle size={15} /> Chiudi</Button>}
            {isAdmin && <Button variant="outline" size="sm" onClick={() => setDelOpen(true)} className="rounded-sm gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="btn-elimina"><Trash size={15} /> Elimina</Button>}
            {isAdmin && t.richiedente_email && <Button variant="outline" size="sm" onClick={blockSender} className="rounded-sm gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="btn-blocca-mittente"><Prohibit size={15} /> Blocca mittente</Button>}
            <Button variant="outline" size="sm" onClick={createIntervento} className="rounded-sm gap-1.5" data-testid="btn-crea-intervento"><Toolbox size={15} /> Crea intervento</Button>
            <div className="ml-auto flex items-center gap-2">
              <PresenceAvatars presence={presence} />
              <Badge item={t.stato} /><Badge item={t.priorita} />
            </div>
          </div>
        </div>

        <div ref={colsRef} className="flex flex-col lg:flex-row gap-5">
          {/* Main */}
          <div className="flex-1 min-w-0 space-y-4">
            <div>
              {editTitle ? (
                <div className="flex items-center gap-2" data-testid="edit-title-row">
                  <Input value={titleVal} onChange={(e) => setTitleVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditTitle(false); }} autoFocus className="h-9 rounded-sm text-lg font-bold" data-testid="edit-title-input" />
                  <Button size="sm" className="h-8 rounded-sm" onClick={saveTitle} data-testid="edit-title-save">Salva</Button>
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditTitle(false)} data-testid="edit-title-cancel">Annulla</Button>
                </div>
              ) : (
                <h1 className="font-heading text-xl font-bold text-slate-800 leading-tight flex items-center gap-2 group" data-testid="ticket-title">{t.oggetto}
                  {!isClosed && <button onClick={() => { setTitleVal(t.oggetto || ""); setEditTitle(true); }} className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground" title="Modifica titolo" data-testid="edit-title-btn"><NotePencil size={16} /></button>}
                </h1>
              )}
              <p className="text-sm text-muted-foreground mt-1">
                <span className="font-medium text-slate-700">{t.cliente_nome || t.richiedente_nome || t.richiedente_email || "—"}</span>
                {t.sorgente?.nome && <> · segnalato via <span className="inline-flex items-center gap-1" data-testid="detail-source"><SourceIcon name={t.sorgente.icon} size={13} /> {t.sorgente.nome}</span></>}
              </p>
            </div>

            {/* Composer */}
            {isCessato ? (
              <Card className="rounded-sm shadow-none p-3 border border-rose-200 bg-rose-50/60" data-testid="cessato-banner">
                <p className="text-sm text-rose-700 font-medium">Cliente cessato</p>
                <p className="text-xs text-rose-600 mt-0.5">Gli operatori non possono rispondere o annotare finché il cliente non viene riattivato. Il ticket resta consultabile.</p>
              </Card>
            ) : !isClosed && (
              <Card ref={composerRef} className={`rounded-sm shadow-none p-0 overflow-hidden border ${mode === "nota" ? "border-amber-200" : "border-border"}`}>
                <div className="flex items-center gap-1 border-b border-border px-2 py-1.5 bg-secondary/30">
                  <button onClick={() => setMode("rispondi")} className={`text-sm px-3 py-1 rounded-sm flex items-center gap-1.5 font-medium ${mode === "rispondi" ? "bg-blue-50 text-blue-700" : "text-muted-foreground hover:bg-secondary"}`} data-testid="composer-rispondi"><ArrowUUpLeft size={14} /> Rispondi</button>
                  <button onClick={() => setMode("nota")} className={`text-sm px-3 py-1 rounded-sm flex items-center gap-1.5 font-medium ${mode === "nota" ? "bg-amber-100 text-amber-700" : "text-muted-foreground hover:bg-secondary"}`} data-testid="composer-nota"><NotePencil size={14} /> Nota</button>
                </div>
                <div className={`p-3 ${mode === "nota" ? "bg-amber-50/40" : ""}`}>
                  {mode === "nota" && (
                    <div className="mb-2 pb-2 border-b border-amber-200/60 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">Notifica utenti TecSuite:</span>
                        <Select value="" onValueChange={(uid) => { const u = (meta.users || []).find((x) => x.id === uid); if (u) setNotifyUsers((prev) => prev.find((p) => p.id === u.id) ? prev : [...prev, { id: u.id, name: u.name || u.email }]); }}>
                          <SelectTrigger className="rounded-sm h-8 w-64" data-testid="notify-users-select"><SelectValue placeholder="Aggiungi collega da notificare" /></SelectTrigger>
                          <SelectContent>{(meta.users || []).filter((u) => u.role !== "client" && !notifyUsers.find((p) => p.id === u.id)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-[11px] text-muted-foreground">Riceveranno notifica in-app ed email</span>
                      </div>
                      {notifyUsers.length > 0 && (
                        <div className="flex flex-wrap gap-1" data-testid="notify-users-chips">
                          {notifyUsers.map((u) => <span key={u.id} className="inline-flex items-center gap-1 text-[11px] bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5 font-medium" data-testid={`notify-user-${u.id}`}>{u.name}<button type="button" onClick={() => setNotifyUsers((prev) => prev.filter((p) => p.id !== u.id))} className="hover:text-emerald-900"><X size={11} /></button></span>)}
                        </div>
                      )}
                    </div>
                  )}
                  {mode === "rispondi" && replyVisible && (
                    <div className="mb-2 pb-2 border-b border-border space-y-2">
                      <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={sendEmail} onCheckedChange={setSendEmail} disabled={mailboxes.length === 0} data-testid="reply-send-email" /> Invia risposta via email al cliente {mailboxes.length === 0 && <span className="text-xs text-muted-foreground">(nessuna casella collegata)</span>}</label>
                      {sendEmail && (
                        <div className="space-y-2 rounded-sm border border-border bg-secondary/20 p-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-10 shrink-0">Da:</span>
                            {mailboxes.length > 1 ? (
                              <Select value={mailboxId} onValueChange={setMailboxId}>
                                <SelectTrigger className="rounded-sm h-8 w-64" data-testid="reply-mailbox"><SelectValue placeholder="Casella mittente" /></SelectTrigger>
                                <SelectContent>{mailboxes.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome || m.email}</SelectItem>)}</SelectContent>
                              </Select>
                            ) : (
                              <span className="text-sm font-medium text-slate-700" data-testid="reply-mailbox-static">{(mailboxes.find((m) => m.id === mailboxId) || mailboxes[0])?.nome || (mailboxes.find((m) => m.id === mailboxId) || mailboxes[0])?.email || "—"}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-10 shrink-0">A:</span>
                            <input value={toAddr} onChange={(e) => changeTo(e.target.value)} placeholder="destinatario@esempio.it" className="h-8 text-sm px-2 rounded-sm border border-border flex-1 min-w-[200px]" data-testid="reply-to" />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-10 shrink-0">CC:</span>
                            <input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="CC (email separate da virgola)" className="h-8 text-sm px-2 rounded-sm border border-border flex-1 min-w-[200px]" data-testid="reply-cc" />
                          </div>
                          {toAddr && t.richiedente_email && toAddr.trim().toLowerCase() !== t.richiedente_email.toLowerCase() && (
                            <p className="text-[11px] text-amber-700" data-testid="reply-cc-hint">Destinatario diverso dal mittente originale ({t.richiedente_email}): aggiunto automaticamente in CC.</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="rich-composer">
                    <RichEditor
                      value={body}
                      onChange={setBody}
                      placeholder={mode === "nota" ? "Nota interna (mai visibile al cliente) — usa @ per menzionare un collega…" : "Scrivi la tua risposta qui…"}
                      testid={mode === "nota" ? "note-input" : "reply-input"}
                      onImageUpload={uploadInlineImage}
                      mentionUsers={mode === "nota" ? (meta.users || []).filter((u) => u.role !== "client") : undefined}
                      onMentionUser={addMention}
                    />
                  </div>
                  {/* Allegati del messaggio in composizione */}
                  <div className="mt-2">
                    <DropZone onFiles={(fl) => addComposerFiles(fl)} className="block">
                    <input ref={composerFileRef} type="file" multiple className="hidden" onChange={(e) => addComposerFiles(e.target.files)} data-testid="composer-file-input" />
                    <Button type="button" variant="outline" size="sm" onClick={() => composerFileRef.current?.click()} disabled={uploadingComposer} className="rounded-sm gap-1.5" data-testid="composer-attach-btn">
                      {uploadingComposer ? <><ArrowsClockwise size={14} className="animate-spin" /> Caricamento…</> : <><Paperclip size={14} /> Allega file</>}
                    </Button>
                    <span className="text-[11px] text-muted-foreground ml-2">Max 100 MB per file{mode === "nota" ? " · resta interno, non visibile al cliente" : ""}</span>
                    </DropZone>
                    {pendingAtts.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="composer-pending-atts">
                        {pendingAtts.map((a) => (
                          <span key={a.id} className="inline-flex items-center gap-1.5 text-xs bg-secondary rounded-sm px-2 py-1 border border-border" data-testid={`pending-att-${a.id}`}>
                            <Paperclip size={12} className="text-muted-foreground" />{a.filename}<span className="text-muted-foreground">{Math.round((a.size || 0) / 1024)} KB</span>
                            <button type="button" onClick={() => removePending(a.id)} className="text-rose-600 hover:text-rose-700" data-testid={`pending-att-remove-${a.id}`}><X size={13} /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {mode === "nota" && mentions.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2" data-testid="mention-selected">
                      {mentions.map((m) => <span key={m.id} className="text-[11px] bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 font-medium">@{m.name}</span>)}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                    {mode === "rispondi"
                      ? <label className="flex items-center gap-2 text-sm text-muted-foreground"><Switch checked={replyVisible} onCheckedChange={setReplyVisible} data-testid="reply-visible" /> Visibile al cliente</label>
                      : <span className="text-xs text-amber-700">Le note interne non sono visibili al cliente</span>}
                    <div className="flex items-center gap-2">
                      {mode === "rispondi" && (
                        <Button onClick={sendAndClose} disabled={sending || isEmptyHtml(body) || uploadingComposer} variant="outline" className="rounded-sm gap-1.5" data-testid="reply-send-close">
                          <PaperPlaneTilt size={15} /> Invia e imposta come chiuso
                        </Button>
                      )}
                      <Button onClick={send} disabled={sending || (isEmptyHtml(body) && pendingAtts.length === 0) || uploadingComposer} className="rounded-sm gap-1.5" data-testid={mode === "nota" ? "note-add" : "reply-send"}>
                        <PaperPlaneTilt size={15} />{mode === "nota" ? "Aggiungi nota" : (sendEmail && replyVisible ? "Invia risposta + email" : "Invia risposta")}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {t?.has_completed_linked_activity && (
              <div className="mb-4 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="ticket-completed-activity-banner">
                <Warning size={22} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0 text-sm text-amber-900">
                  <div className="font-semibold">
                    {t.completed_linked_activities_count > 1
                      ? `Sono presenti ${t.completed_linked_activities_count} interventi collegati completati.`
                      : "Un intervento collegato a questo ticket risulta completato."}
                  </div>
                  <div className="text-amber-800 mt-0.5">Verifica l'esito dell'intervento e valuta se risolvere o chiudere il ticket.</div>
                  {(t.completed_linked_activities || []).slice(0, 3).map((a) => (
                    <div key={a.id} className="mt-1 text-xs text-amber-800">
                      Intervento <span className="font-medium">{a.numero}</span> completato{a.data_completamento ? ` il ${fmtDate(a.data_completamento)}` : ""}{a.tecnici_nomi ? ` · ${a.tecnici_nomi}` : ""}
                      {" — "}<button onClick={() => nav(a.link)} className="underline hover:text-amber-950" data-testid={`banner-atv-link-${a.id}`}>apri</button>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" onClick={() => setActiveTab("interventi")} className="rounded-sm border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0" data-testid="banner-goto-interventi">Vai agli interventi collegati</Button>
              </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="rounded-sm">
                <TabsTrigger value="conversazione" data-testid="tab-conversazione"><ChatCircle size={15} className="mr-1.5" />Conversazione</TabsTrigger>
                <TabsTrigger value="interventi" data-testid="tab-interventi"><Toolbox size={15} className="mr-1.5" />Interventi{activities.length ? ` (${activities.length})` : ""}</TabsTrigger>
                <TabsTrigger value="allegati" data-testid="tab-allegati"><Paperclip size={15} className="mr-1.5" />Allegati{atts.length ? ` (${atts.length})` : ""}</TabsTrigger>
                <TabsTrigger value="storico" data-testid="tab-storico"><ClockCounterClockwise size={15} className="mr-1.5" />Storico</TabsTrigger>
                <TabsTrigger value="sla" data-testid="tab-sla-eventi"><Timer size={15} className="mr-1.5" />SLA</TabsTrigger>
                <TabsTrigger value="automazioni" data-testid="tab-automazioni" className="opacity-70 data-[state=active]:opacity-100"><Lightning size={15} className="mr-1.5" />Automazioni</TabsTrigger>
              </TabsList>

              <TabsContent value="conversazione" className="mt-4 space-y-3">
                {msgs.length === 0 && <p className="text-sm text-muted-foreground">Nessun messaggio.</p>}
                {[...msgs].reverse().map((m) => {
                  const mm = MSG_META[m.tipo] || MSG_META.sistema;
                  const canEditMsg = !isClosed && editingMsgId !== m.id && ((m.tipo === "nota_interna" && m.autore_user_id === user?.id) || (m.tipo === "cliente" && m.canale !== "email"));
                  return (
                    <div key={m.id} id={`hd-msg-${m.id}`} className={`flex gap-3 scroll-mt-24 transition-all duration-500 ${m.tipo === "nota_interna" ? "flex-row-reverse ml-12 sm:ml-48" : "mr-12 sm:mr-48"} ${highlightMsgId === m.id ? "ring-2 ring-primary ring-offset-2 rounded-sm" : ""}`} data-testid={`msg-${m.id}`}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-slate-700 shrink-0 mt-0.5" style={{ backgroundColor: avatarColor(m.autore_nome) }}>{initial(m.autore_nome)}</div>
                      <Card className={`rounded-sm shadow-none p-3 border-l-4 flex-1 ${mm.border} ${mm.bg}`}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="font-semibold text-slate-700">{m.autore_nome || "—"}<span className="font-normal text-muted-foreground ml-1.5">· {mm.label}</span>
                            {m.canale === "email" && m.tipo === "cliente" && <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700">Email ricevuta</span>}
                            {m.tipo === "operatore" && m.inviato_email && <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">Email inviata</span>}
                            {m.tipo === "operatore" && m.esito_invio_email && !m.inviato_email && <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-rose-100 text-rose-700">Invio fallito</span>}
                          </span>
                          <span className="flex items-center gap-2 text-muted-foreground">
                            {canEditMsg && <button onClick={() => startEditMsg(m)} className="hover:text-foreground" title="Modifica" data-testid={`msg-edit-btn-${m.id}`}><NotePencil size={13} /></button>}
                            <span title={fmtDateTime(m.created_at)}>{timeAgo(m.created_at)}</span>
                          </span>
                        </div>
                        {msgAttachments(m).length > 0 && (
                          <div className="mb-2 pb-2 border-b border-border/60" data-testid={`msg-atts-${m.id}`}>
                            <div className="text-[11px] font-medium text-muted-foreground mb-1 flex items-center gap-1"><Paperclip size={12} /> {msgAttachments(m).length} allegat{msgAttachments(m).length === 1 ? "o" : "i"}</div>
                            <div className="space-y-1">
                              {msgAttachments(m).map((a) => (
                                <div key={a.id} className="flex items-center gap-2 text-xs" data-testid={`msg-att-${a.id}`}>
                                  <Paperclip size={13} className="text-muted-foreground shrink-0" />
                                  <button onClick={() => previewAtt(a)} className="text-blue-600 hover:underline truncate max-w-[320px] text-left" title={`Anteprima ${a.filename}`} data-testid={`msg-att-link-${a.id}`}>{a.filename}</button>
                                  <span className="text-muted-foreground shrink-0">{Math.round((a.size || 0) / 1024)} KB</span>
                                  <button onClick={() => previewAtt(a)} title="Anteprima web" className="text-muted-foreground hover:text-blue-600 shrink-0" data-testid={`msg-att-preview-${a.id}`}><Eye size={14} /></button>
                                  <button onClick={() => downloadAtt(a)} title="Scarica" className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`msg-att-dl-${a.id}`}><DownloadSimple size={14} /></button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {editingMsgId === m.id ? (
                          <div className="space-y-2" data-testid={`msg-edit-${m.id}`}>
                            <div className="rich-composer">
                              <RichEditor value={editBody} onChange={setEditBody} onImageUpload={uploadInlineImage}
                                mentionUsers={m.tipo === "nota_interna" ? (meta.users || []).filter((u) => u.role !== "client") : undefined}
                                onMentionUser={addEditMention} testid={`msg-edit-input-${m.id}`} />
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" className="h-7 rounded-sm" disabled={savingEdit} onClick={() => saveEditMsg(m)} data-testid={`msg-edit-save-${m.id}`}>Salva</Button>
                              <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditingMsgId(null)} data-testid={`msg-edit-cancel-${m.id}`}>Annulla</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {m.corpo_html
                              ? <SafeHtml html={m.corpo_html} className="text-sm text-slate-700 hd-email-body" />
                              : <div className="text-sm whitespace-pre-wrap text-slate-700">{m.tipo === "nota_interna" && m.mentioned_users?.length ? highlightMentions(m.corpo, m.mentioned_users) : m.corpo}</div>}
                            {m.edited_at && <div className="text-[10px] text-muted-foreground italic mt-1" data-testid={`msg-edited-${m.id}`}>(modificato{m.edited_by_nome ? ` da ${m.edited_by_nome}` : ""})</div>}
                          </>
                        )}
                        {m.esito_invio_email && !m.inviato_email && <div className="text-[11px] text-rose-600 mt-1">{m.esito_invio_email}</div>}
                      </Card>
                    </div>
                  );
                })}
              </TabsContent>

              <TabsContent value="allegati" className="mt-4">
                <DropZone onFiles={(fl) => onUpload({ target: { files: fl, value: "" } })} className="block">
                <Card className="rounded-sm shadow-none p-4">
                  <input ref={fileRef} type="file" multiple className="hidden" onChange={onUpload} data-testid="att-input" />
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm gap-1 mb-1" data-testid="att-upload">
                    {uploading ? <><ArrowsClockwise size={14} className="animate-spin" /> Caricamento in corso…</> : <><Paperclip size={14} /> Carica file</>}
                  </Button>
                  <p className="text-[11px] text-muted-foreground mb-3">Dimensione massima per file: 100 MB.</p>
                  {atts.length === 0 ? <p className="text-sm text-muted-foreground">Nessun allegato.</p> : <div className="space-y-2">
                    {atts.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 p-2 rounded-sm border border-border" data-testid={`att-${a.id}`}>
                        <Paperclip size={14} className="text-muted-foreground shrink-0" /><span className="text-sm flex-1 truncate">{a.filename}</span>
                        <span className="text-[11px] text-muted-foreground">{Math.round((a.size || 0) / 1024)} KB · {a.uploaded_by}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Anteprima web" onClick={() => previewAtt(a)} data-testid={`att-preview-${a.id}`}><Eye size={15} /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Scarica" onClick={() => downloadAtt(a)} data-testid={`att-dl-${a.id}`}><DownloadSimple size={15} /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => delAtt(a.id)} data-testid={`att-del-${a.id}`}><Trash size={14} /></Button>
                      </div>
                    ))}
                  </div>}
                </Card>
                </DropZone>
              </TabsContent>

              <TabsContent value="storico" className="mt-4">
                <Card className="rounded-sm shadow-none p-4 space-y-2">
                  {hist.length === 0 ? <p className="text-sm text-muted-foreground">Nessun evento.</p> : hist.map((h) => (
                    <div key={h.id} className="text-xs border-l-2 border-border pl-2.5 py-0.5" data-testid={`hist-${h.id}`}>
                      <span className="font-medium">{histActionLabel(h.action)}</span>{h.old_value != null && <span className="text-muted-foreground"> · da {idName(h.old_value)} a {idName(h.new_value)}</span>}{h.note && <span className="text-muted-foreground"> · {h.note}</span>}
                      <div className="text-muted-foreground/70">{fmtDateTime(h.timestamp)} · {h.user_name}</div>
                    </div>
                  ))}
                </Card>
              </TabsContent>

              <TabsContent value="automazioni" className="mt-4">
                <Card className="rounded-sm shadow-none p-4 space-y-2">
                  {logs.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna automazione eseguita su questo ticket.</p> : logs.map((l) => (
                    <div key={l.id} className="text-xs border-l-2 border-border pl-2.5 py-0.5"><span className="font-medium">{l.esito}</span> · {l.dettagli}<div className="text-muted-foreground/70">{fmtDateTime(l.executed_at)}</div></div>
                  ))}
                </Card>
              </TabsContent>

              <TabsContent value="sla" className="mt-4">
                <Card className="rounded-sm shadow-none p-4 space-y-2" data-testid="sla-events-list">
                  {slaEvents.length === 0 ? <p className="text-sm text-muted-foreground">Nessun evento SLA.</p> : slaEvents.map((e) => (
                    <div key={e.id} className="text-xs border-l-2 border-border pl-2.5 py-0.5" data-testid={`sla-event-${e.id}`}>
                      <span className="font-medium">{e.event_type}</span>{e.descrizione && <span className="text-muted-foreground"> · {e.descrizione}</span>}
                      {e.new_value && <span className="text-muted-foreground"> · {e.new_value}</span>}
                      <div className="text-muted-foreground/70">{fmtDateTime(e.created_at)}</div>
                    </div>
                  ))}
                </Card>
              </TabsContent>

              <TabsContent value="interventi" className="mt-4 space-y-3">
                {activities.some((a) => a.completata) && !["Chiuso", "Risolto"].includes((t.stato || {}).nome) && !suggestDismissed && (
                  <Card className="rounded-sm shadow-none p-3 bg-amber-50 border-amber-200 flex items-center gap-3" data-testid="resolve-suggestion">
                    <CheckCircle size={20} className="text-amber-600 shrink-0" />
                    <p className="text-sm text-amber-800 flex-1">Uno o più interventi collegati risultano completati. Vuoi risolvere il ticket?</p>
                    <Button size="sm" onClick={resolveFromActivity} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="resolve-ticket-btn">Risolvi ticket</Button>
                    <Button size="sm" variant="ghost" onClick={() => setSuggestDismissed(true)} data-testid="ignore-suggestion-btn">Ignora</Button>
                  </Card>
                )}
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setLinkDlg(true); searchLinkActivities(""); }} className="rounded-sm gap-1.5" data-testid="link-intervento-btn"><LinkSimple size={15} /> Collega intervento esistente</Button>
                  <Button size="sm" onClick={createIntervento} className="rounded-sm gap-1.5" data-testid="crea-intervento-tab-btn"><Plus size={15} /> Crea intervento</Button>
                </div>
                <Card className="rounded-sm shadow-none divide-y divide-border">
                  {activities.length === 0 ? <p className="text-sm text-muted-foreground p-4">Nessun intervento collegato.</p> : activities.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 p-3" data-testid={`linked-activity-${a.id}`}>
                      <Toolbox size={18} className="text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium flex items-center gap-2">Intervento #{a.numero || "—"}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${a.completata ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{a.stato}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">{a.data || "—"} · {a.tecnici_nomi || "nessun tecnico"} · {Math.round((a.tempo_totale_minuti || 0) / 60 * 10) / 10}h · {a.richiesta_preview}</div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => nav(`/interventi/${a.id}`)} title="Apri intervento" data-testid={`open-activity-${a.id}`}><ArrowSquareOut size={15} /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => unlinkActivity(a.id)} title="Scollega" data-testid={`unlink-activity-${a.id}`}><Trash size={15} /></Button>
                    </div>
                  ))}
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Maniglia resize (solo desktop) */}
          <div onMouseDown={startPropResize} onDoubleClick={() => setPropWidth(PROP_DEFAULT_W)}
            className="hidden lg:block w-1.5 shrink-0 self-stretch rounded cursor-col-resize bg-border/50 hover:bg-primary/50 transition-colors"
            title="Trascina per ridimensionare · doppio click per ripristinare" data-testid="prop-resize-handle" />

          {/* PROPRIETÀ sidebar */}
          <div className="w-full shrink-0 flex flex-col gap-4" style={isLg ? { width: `${propWidth}px` } : undefined} data-testid="ticket-properties-col">
            <Card className="rounded-sm shadow-none p-4 space-y-3.5 sticky top-32" data-testid="ticket-sidebar">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border pb-2">Proprietà</div>
              <div><Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Cliente</Label>
                <div className="mt-1"><ClientPicker clients={meta.clients} value={t.cliente_id} onChange={(v) => patch("cliente_id", v)} /></div>
              </div>
              <div className="rounded-sm border border-blue-200 bg-blue-50/60 p-3 space-y-2" data-testid="referente-box">
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 flex items-center gap-1.5"><User size={13} weight="bold" /> Referente</Label>
                  {!isClosed && !editReq && <button onClick={startEditReq} className="text-blue-600 hover:text-blue-800" title="Modifica referente" data-testid="edit-referente-btn"><NotePencil size={16} /></button>}
                </div>
                {editReq ? (
                  <div className="space-y-1.5" data-testid="edit-referente-form">
                    <Input value={reqForm.nome} onChange={(e) => setReqForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Persona di riferimento" className="h-9 rounded-sm bg-white" data-testid="edit-referente-nome" />
                    <Input value={reqForm.email} onChange={(e) => setReqForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="h-9 rounded-sm bg-white" data-testid="edit-referente-email" />
                    <Input value={reqForm.telefono} onChange={(e) => setReqForm((f) => ({ ...f, telefono: e.target.value }))} placeholder="Telefono" className="h-9 rounded-sm bg-white" data-testid="edit-referente-telefono" />
                    <div className="flex gap-2 pt-0.5">
                      <Button size="sm" className="h-8 rounded-sm" onClick={saveReq} data-testid="edit-referente-save">Salva</Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditReq(false)} data-testid="edit-referente-cancel">Annulla</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {t.richiedente_nome && <div className="text-base font-semibold text-slate-800 leading-tight" data-testid="referente-nome">{t.richiedente_nome}</div>}
                    {t.richiedente_email && <a href={`mailto:${t.richiedente_email}`} className="flex items-center gap-2 text-sm text-blue-700 hover:underline break-all" data-testid="referente-email"><Envelope size={15} className="shrink-0 text-blue-500" />{t.richiedente_email}</a>}
                    {t.richiedente_telefono && <a href={`tel:${t.richiedente_telefono}`} className="flex items-center gap-2 text-sm text-slate-700 hover:underline" data-testid="referente-telefono"><Phone size={15} className="shrink-0 text-blue-500" />{t.richiedente_telefono}</a>}
                    {!t.richiedente_nome && !t.richiedente_email && !t.richiedente_telefono && <div className="text-sm italic text-muted-foreground">Nessun referente</div>}
                  </div>
                )}
              </div>
              <Prop label="Stato" field="stato_id" coll="statuses" allowNone={false} required />
              <Prop label="Priorità" field="priorita_id" coll="priorities" allowNone={false} required />
              <Prop label="Sorgente" field="sorgente_id" coll="sources" allowNone={false} />
              <Prop label="Gruppo / Ufficio" field="gruppo_id" coll="groups" />
              <div><Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Assegnato a</Label>
                <Select value={t.assegnato_a_user_id || "none"} onValueChange={(v) => patch("assegnato_a_user_id", v === "none" ? null : v)}>
                  <SelectTrigger className="mt-1 rounded-sm h-9 bg-white" data-testid="prop-assegnato"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Non assegnato</SelectItem>{meta.users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-1"><Switch checked={!!t.visibile_area_cliente} onCheckedChange={(v) => patch("visibile_area_cliente", v)} data-testid="prop-visibile" /><Label className="text-sm">Visibile in area cliente</Label></div>
              <div className="text-[11px] text-muted-foreground pt-2 border-t border-border space-y-0.5">
                <div>Creato: {fmtDateTime(t.data_creazione)}</div>
                <div>Aggiornato: {fmtDateTime(t.data_ultimo_aggiornamento)}</div>
                {t.data_risoluzione && <div>Risolto: {fmtDateTime(t.data_risoluzione)}</div>}
                {t.data_chiusura && <div>Chiuso: {fmtDateTime(t.data_chiusura)}</div>}
              </div>
            </Card>

            <Card className="rounded-sm shadow-none p-4 space-y-2.5 order-first" data-testid="sla-box">
              <div className="flex items-center justify-between border-b border-border pb-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Timer size={13} /> SLA</div>
                <button onClick={recalcSla} className="text-muted-foreground hover:text-foreground" title="Ricalcola SLA" data-testid="sla-recalc-btn"><ArrowsClockwise size={14} /></button>
              </div>
              {(!t.sla_status || t.sla_status === "none") ? (
                <p className="text-xs text-muted-foreground">Nessuno SLA applicato.</p>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Stato</span>
                    <SlaBadge status={t.sla_status} testid="sla-box-badge" />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Prima risposta</span>
                    <span className={t.sla_first_response_met === false ? "text-rose-600" : t.sla_first_response_met ? "text-emerald-600" : "text-slate-700"}>
                      {t.sla_first_response_met === true ? "✓ effettuata" : t.sla_first_response_due_at ? fmtDateTime(t.sla_first_response_due_at) : "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Risoluzione</span>
                    <span className={t.sla_resolution_met === false ? "text-rose-600" : t.sla_resolution_met ? "text-emerald-600" : "text-slate-700"}>
                      {t.sla_resolved_at ? (t.sla_resolution_met ? "✓ entro SLA" : "✗ fuori SLA") : t.sla_resolution_due_at ? fmtDateTime(t.sla_resolution_due_at) : "—"}
                    </span>
                  </div>
                  {t.sla_paused && <div className="text-xs text-slate-600">⏸ SLA in pausa dal {fmtDateTime(t.sla_paused_since)}</div>}
                  {(t.sla_total_paused_minutes || 0) > 0 && <div className="text-[11px] text-muted-foreground">Sospensione totale: {t.sla_total_paused_minutes} min</div>}
                </>
              )}
              {isAdmin && (
                <div className="pt-2 border-t border-border">
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Policy SLA</Label>
                  <Select value={t.sla_policy_id || "none"} onValueChange={setSlaPolicy}>
                    <SelectTrigger className="mt-1 rounded-sm h-9 bg-white" data-testid="sla-policy-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Nessuno SLA</SelectItem>{meta.slaPolicies.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={linkDlg} onOpenChange={setLinkDlg}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="link-activity-dialog">
          <DialogHeader>
            <DialogTitle>Collega intervento esistente</DialogTitle>
            <DialogDescription>Cerca per numero, richiesta o descrizione. Vengono proposti gli interventi dello stesso cliente.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={linkQ} onChange={(e) => searchLinkActivities(e.target.value)} placeholder="Cerca intervento…" className="pl-8 rounded-sm" data-testid="link-activity-search" autoFocus />
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-border border border-border rounded-sm">
            {linkResults.length === 0 ? <p className="text-sm text-muted-foreground p-4">Nessun intervento trovato.</p> : linkResults.map((a) => (
              <button key={a.id} onClick={() => linkActivity(a.id, a.cliente_id)} className="w-full text-left p-3 hover:bg-secondary flex items-center gap-3" data-testid={`link-result-${a.id}`}>
                <Toolbox size={16} className="text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">#{a.numero || "—"} · {a.cliente_nome || "—"} {a.cliente_id && t?.cliente_id && a.cliente_id !== t.cliente_id && <span className="text-[10px] text-amber-600">(cliente diverso)</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{a.data} · {a.stato} · {a.richiesta_preview}</div>
                </div>
                <LinkSimple size={15} className="text-blue-600 shrink-0" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="detail-delete-dialog">
          <DialogHeader>
            <DialogTitle>Elimina ticket</DialogTitle>
            <DialogDescription>Sei sicuro di voler eliminare il ticket <strong>{t.numero_ticket}</strong> — "{t.oggetto}"? Verrà rimosso dalla lista.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)} className="rounded-sm" data-testid="detail-delete-cancel">Annulla</Button>
            <Button onClick={doDelete} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="detail-delete-confirm">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={csatAsk} onOpenChange={setCsatAsk}>
        <DialogContent className="rounded-sm max-w-md" data-testid="csat-ask-dialog">
          <DialogHeader>
            <DialogTitle>Inviare il sondaggio di soddisfazione?</DialogTitle>
            <DialogDescription>Il ticket è stato chiuso. Vuoi inviare al cliente <strong>{t.richiedente_email}</strong> il sondaggio di soddisfazione via email?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCsatAsk(false)} className="rounded-sm" data-testid="csat-ask-no">No, grazie</Button>
            <Button onClick={sendCsat} className="rounded-sm" data-testid="csat-ask-yes">Sì, invia sondaggio</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </InternalLayout>
  );
}
